// axios is retained for the direct-to-blob SAS PUT below (uploadFileToSas); all API calls go through the
// shared createApiInstance factory.
import axios from "axios";
import { createApiInstance } from "./apiClient";

// Generic, public direct-to-blob media upload (backend: api/entitymedia). Uploads are staged under a
// server-generated submissionId — never a caller-named entity — so an untrusted caller can never attach
// media to an existing record. The flow is: requestEntityMediaUpload (reserve a blob + short-lived SAS)
// -> PUT the bytes straight to the SAS URL -> completeEntityMediaUpload (server verifies size/type/malware
// and publishes). An authenticated caller can then claim the submission onto itself. This is the storefront
// twin of the admin product-media choreography; the blob seam (user-delegation SAS) is implemented server-side.
const url = process.env.REACT_APP_ORDER_API_URL;

const entityMediaInstance = () =>
  createApiInstance({ baseURL: `${url}/api/entitymedia`, retry: true });

const asMediaResult = (response) => {
  const ok =
    response.status === 200 || response.status === 201 || response.status === 204;
  return ok
    ? { error: false, success: true, message: response.data?.message, data: response.data?.data }
    : { error: true, success: false, message: response.data?.message };
};

const logMediaError = (error) => {
  if (error.response) {
    console.error("Server Error:", error.response.status);
    console.error("Server Error Body:", error.response.data);
  } else if (error.request) {
    console.error("Network Error:", error.request);
  } else {
    console.error("Error:", error.message);
  }
};

// A 400 from the upload request is a validation dictionary (file type/size/extension policy). Parse it so the
// real reason reaches the caller instead of a generic "could not upload".
const parseValidationErrors = (data) => {
  if (data && Array.isArray(data.errors)) {
    const problemMessages = data.errors.map((e) => e?.message).filter(Boolean);
    return {
      errors: null,
      message: problemMessages.length ? problemMessages.join(" ") : data.title || null,
    };
  }

  const dict =
    data && typeof data === "object" && data.errors && typeof data.errors === "object"
      ? data.errors
      : data;
  if (!dict || typeof dict !== "object") return { errors: null, message: null };
  const messages = Object.values(dict)
    .flatMap((v) => (Array.isArray(v) ? v : []))
    .filter(Boolean);
  return {
    errors: messages.length ? dict : null,
    message: messages.length ? messages.join(" ") : data.title || data.message || null,
  };
};

// Reserves a blob location + short-lived SAS and returns an EntityMediaUploadTicketDto
// { submissionId, upload: { mediaId, uploadUrl, blobContainer, blobName, expiresAt } }.
export const requestEntityMediaUpload = async ({
  originalFileName,
  contentType,
  expectedSizeBytes = null,
  mediaTypeId = 1,
}) => {
  try {
    const response = await entityMediaInstance().post(`/uploads`, {
      originalFileName,
      contentType,
      expectedSizeBytes,
      mediaTypeId,
    });
    return asMediaResult(response);
  } catch (error) {
    logMediaError(error);
    const { errors, message } = parseValidationErrors(error?.response?.data);
    return { error: true, success: false, errors, message: message || "Could not start the upload." };
  }
};

// Direct-to-blob PUT to the SAS URL from requestEntityMediaUpload. Deliberately a bare axios instance — no
// API auth/website headers; the SAS itself authorizes the single Create+Write. Azure requires x-ms-blob-type
// for a block-blob PUT. (Runtime prerequisite: storage CORS must allow this origin to PUT.)
const uploadFileToSas = async ({ uploadUrl, file, contentType }) => {
  try {
    await axios.create().put(uploadUrl, file, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": contentType || file?.type || "application/octet-stream",
      },
    });
    return { error: false, success: true };
  } catch (error) {
    logMediaError(error);
    return { error: true, success: false, message: "Could not upload the file to storage." };
  }
};

// Called after the bytes are PUT to the SAS URL; the server verifies magic-byte/size/type (and malware) and
// publishes the media. A failed verification returns 400 (the blob is removed server-side).
export const completeEntityMediaUpload = async ({ submissionId, mediaId }) => {
  try {
    const response = await entityMediaInstance().post(
      `/uploads/${submissionId}/${mediaId}/complete`,
    );
    return asMediaResult(response);
  } catch (error) {
    logMediaError(error);
    return { error: true, success: false, message: "The file could not be verified." };
  }
};

// Claims a completed submission onto the authenticated caller's own user record (requires auth).
export const claimEntityMediaToCurrentUser = async ({ submissionId }) => {
  try {
    const response = await entityMediaInstance().post(`/uploads/${submissionId}/claim`);
    return asMediaResult(response);
  } catch (error) {
    logMediaError(error);
    return { error: true, success: false, message: "Could not claim the upload." };
  }
};

// Full direct-to-blob upload of one file: reserve a SAS ticket -> PUT the bytes to it -> complete. Returns
// the staged { submissionId, mediaId } on success so the caller can attach it (e.g. assign it to a
// trade-application document slot, or claim it onto the current user).
export const uploadEntityMediaFile = async ({ file, mediaTypeId = 1 }) => {
  const contentType = file?.type || "application/octet-stream";

  const ticket = await requestEntityMediaUpload({
    originalFileName: file?.name,
    contentType,
    expectedSizeBytes: file?.size ?? null,
    mediaTypeId,
  });

  const submissionId = ticket?.data?.submissionId;
  const mediaId = ticket?.data?.upload?.mediaId;
  const uploadUrl = ticket?.data?.upload?.uploadUrl;

  if (!ticket?.success || !submissionId || !mediaId || !uploadUrl) {
    return { error: true, success: false, errors: ticket?.errors ?? null, message: ticket?.message || "Could not reserve the upload." };
  }

  const uploaded = await uploadFileToSas({ uploadUrl, file, contentType });
  if (!uploaded.success) {
    return uploaded;
  }

  const completed = await completeEntityMediaUpload({ submissionId, mediaId });
  if (!completed.success) {
    return completed;
  }

  return { error: false, success: true, submissionId, mediaId, message: completed.message };
};
